import { useRef, useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input, Select } from '@/components/ui/input'
import { createUnit, updateUnit } from '@/features/units/units-api'
import { describeWriteError } from '@/features/users/permissions'
import { TRANSLATION_LANGUAGES } from '@/lib/constants'
import { TranslatedInput } from '@/components/TranslatedField'
import type { UnitOfMeasure } from '@/lib/types'

/** Create/edit a unit of measurement. The symbol is what estimates print next to a quantity. */
export function UnitEditDialog({
  unit,
  actor,
  onClose,
}: {
  /** `null` = create a new unit. */
  unit: UnitOfMeasure | null
  actor: { uid: string; label: string }
  onClose: () => void
}) {
  const [name, setName] = useState(unit?.name ?? '')
  const [symbol, setSymbol] = useState(unit?.symbol ?? '')
  const [isActive, setActive] = useState(unit?.isActive ?? true)
  const [nameI18n, setNameI18n] = useState<Record<string, string>>(unit?.nameI18n ?? {})
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Quick-insert of ² / ³, as in the mobile app: the snippet lands at the cursor of the field that
  // was focused last (name or symbol), symbol by default.
  const nameRef = useRef<HTMLInputElement>(null)
  const symbolRef = useRef<HTMLInputElement>(null)
  const [insertTarget, setInsertTarget] = useState<'name' | 'symbol'>('symbol')

  const insert = (snippet: string) => {
    const input = (insertTarget === 'name' ? nameRef : symbolRef).current
    const current = insertTarget === 'name' ? name : symbol
    const setValue = insertTarget === 'name' ? setName : setSymbol
    const start = input?.selectionStart ?? current.length
    const end = input?.selectionEnd ?? current.length
    setValue(current.slice(0, start) + snippet + current.slice(end))
    // Restore focus and put the caret right after the inserted character once React re-rendered.
    requestAnimationFrame(() => {
      if (!input) return
      input.focus()
      const caret = start + snippet.length
      input.setSelectionRange(caret, caret)
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const draft = {
      name: name.trim(),
      nameI18n: sanitize(nameI18n),
      symbol: symbol.trim(),
      isActive,
    }
    try {
      if (unit) {
        await updateUnit(unit.id, draft, actor)
      } else {
        await createUnit(draft, actor)
      }
      onClose()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{unit ? 'Единица измерения' : 'Новая единица'}</DialogTitle>
          <DialogDescription>
            Название пишется по-русски, переводы необязательны. Символ подставляется в материалы и
            печатается в сметах.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Название (ru)</span>
              <Input
                ref={nameRef}
                value={name}
                onChange={(event) => setName(event.target.value)}
                onFocus={() => setInsertTarget('name')}
                autoFocus
              />
            </label>
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">Символ</span>
              <Input
                ref={symbolRef}
                value={symbol}
                onChange={(event) => setSymbol(event.target.value)}
                onFocus={() => setInsertTarget('symbol')}
                className="sm:w-28"
                placeholder="м²"
              />
            </label>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground flex-1 text-xs">
              Вставить степень в поле «{insertTarget === 'name' ? 'Название' : 'Символ'}»
            </span>
            {['²', '³'].map((snippet) => (
              <Button
                key={snippet}
                type="button"
                variant="outline"
                size="sm"
                // preventDefault keeps focus (and the selection) in the text field.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insert(snippet)}
                aria-label={`Вставить ${snippet}`}
              >
                x{snippet}
              </Button>
            ))}
          </div>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Состояние</span>
            <Select
              value={isActive ? 'active' : 'inactive'}
              onChange={(event) => setActive(event.target.value === 'active')}
            >
              <option value="active">Активна</option>
              <option value="inactive">Скрыта</option>
            </Select>
            <span className="text-muted-foreground text-xs">
              Скрытая единица остаётся у материалов, которые её уже используют, но не предлагается
              при выборе.
            </span>
          </label>

          <div className="grid gap-2 rounded-lg border p-3">
            <span className="text-sm font-medium">Переводы названия</span>
            {TRANSLATION_LANGUAGES.map((language) => (
              <TranslatedInput
                key={language.tag}
                value={nameI18n[language.tag] ?? ''}
                onChange={(event) =>
                  setNameI18n({ ...nameI18n, [language.tag]: event.target.value })
                }
                source={name}
                lang={language.tag}
                onTranslated={(text) => setNameI18n((prev) => ({ ...prev, [language.tag]: text }))}
                placeholder={language.label}
                aria-label={`Название (${language.label})`}
              />
            ))}
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Не сохранено</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || name.trim().length === 0 || symbol.trim().length === 0}
          >
            {isSaving ? <Loader2 className="animate-spin" /> : null}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function sanitize(values: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [tag, value] of Object.entries(values)) {
    const trimmed = value.trim()
    if (trimmed.length > 0) result[tag] = trimmed
  }
  return result
}
